import { computed } from "vue";
import { useGlobalSystemSettingsQuery } from "@/graphql";

export type SystemColor = {
  name: string;
  hex: string;
};

export type SystemColorPalette = {
  designColors: SystemColor[];
  standardColors: SystemColor[];
  standardColorsFull: SystemColor[];
};

const defaultDesignColorHexValues = [
  "#ffffff",
  "#000000",
  "#e7e6e6",
  "#44546a",
  "#4472c4",
  "#ed7d31",
  "#a5a5a5",
  "#ffc009",
  "#5b9bd5",
  "#70ad47",
];

export function toColors(names: readonly string[], hexValues: readonly string[]): SystemColor[] {
  return hexValues.map((hex, index) => ({
    name: names[index] ?? "",
    hex,
  }));
}

export function toSystemColorPalette(settings: {
  designColorNames: readonly string[];
  designColorHexValues: readonly string[];
  standardColorNames: readonly string[];
  standardColorHexValues: readonly string[];
  standardColorFullNames: readonly string[];
  standardColorFullHexValues: readonly string[];
}): SystemColorPalette {
  return {
    designColors: toColors(settings.designColorNames, settings.designColorHexValues),
    standardColors: toColors(settings.standardColorNames, settings.standardColorHexValues),
    standardColorsFull: toColors(
      settings.standardColorFullNames,
      settings.standardColorFullHexValues,
    ),
  };
}

export function useGlobalSystemSettings() {
  const query = useGlobalSystemSettingsQuery({
    fetchPolicy: "cache-first",
  });

  const settings = computed(() => query.result.value?.GlobalSystemSettings);
  const businessYearStartMonth = computed(() => settings.value?.businessYearStartMonth ?? 10);
  const placeholderImage = computed(() => settings.value?.placeholderImage ?? null);
  const lisaChartSettings = computed(() => ({
    hideXAxisLabelsMinified: settings.value?.hideXAxisLabelsMinified ?? false,
    hideYAxisLabelsMinified: settings.value?.hideYAxisLabelsMinified ?? false,
  }));
  const colorPalette = computed<SystemColorPalette>(() =>
    settings.value
      ? toSystemColorPalette(settings.value)
      : {
          designColors: toColors(
            Array.from({ length: 10 }, () => ""),
            defaultDesignColorHexValues,
          ),
          standardColors: [],
          standardColorsFull: [],
        },
  );

  return {
    ...query,
    settings,
    businessYearStartMonth,
    placeholderImage,
    lisaChartSettings,
    colorPalette,
  };
}
